import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { 
  Search, 
  Wallet, 
  TrendingUp, 
  TrendingDown, 
  Plus, 
  ArrowUpRight, 
  ArrowDownRight,
  Calendar,
  FileText,
  RefreshCw,
  Trash2,
  Play,
  CheckCircle,
  AlertCircle,
  Download,
  FileSpreadsheet,
  Eye,
  X,
  Loader2,
  ExternalLink,
  Pencil,
  History,
  User
} from "lucide-react";
import api from "../lib/api";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const BRAND_COLOR = "#c11718";

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountDetail, setAccountDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [addTxDialogOpen, setAddTxDialogOpen] = useState(false);
  const [addRentDialogOpen, setAddRentDialogOpen] = useState(false);
  const [generateRentDialogOpen, setGenerateRentDialogOpen] = useState(false);
  const [rentStatus, setRentStatus] = useState(null);
  const [rentStatusLoading, setRentStatusLoading] = useState(false);
  const [generatingRent, setGeneratingRent] = useState(false);
  // PDF Preview State
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewGuest, setPdfPreviewGuest] = useState(null);
  const [txForm, setTxForm] = useState({
    type: "credit",
    amount: "",
    date: format(new Date(), "yyyy-MM-dd"),
    description: "",
    reference: ""
  });
  const [rentMonth, setRentMonth] = useState(format(new Date(), "yyyy-MM"));
  const [bulkRentMonth, setBulkRentMonth] = useState(format(new Date(), "yyyy-MM"));
  
  // Edit Transaction State
  const [editTxDialogOpen, setEditTxDialogOpen] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [editTxForm, setEditTxForm] = useState({
    type: "",
    amount: "",
    date: "",
    description: "",
    reference: ""
  });
  
  // Audit Log State
  const [auditLogDialogOpen, setAuditLogDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await api.get("/accounts");
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      toast.error("Konten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountDetail = async (guestId) => {
    setDetailLoading(true);
    try {
      const response = await api.get(`/accounts/${guestId}`);
      setAccountDetail(response.data);
    } catch (error) {
      console.error("Error fetching account detail:", error);
      toast.error("Kontodetails konnten nicht geladen werden");
    } finally {
      setDetailLoading(false);
    }
  };

  const openAccountDetail = (account) => {
    setSelectedAccount(account);
    fetchAccountDetail(account.guest_id);
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    if (!selectedAccount) return;
    
    try {
      await api.post("/accounts/transaction", {
        guest_id: selectedAccount.guest_id,
        type: txForm.type,
        amount: parseFloat(txForm.amount),
        date: txForm.date,
        description: txForm.description,
        reference: txForm.reference || null
      });
      
      toast.success("Transaktion hinzugefügt");
      setAddTxDialogOpen(false);
      setTxForm({
        type: "credit",
        amount: "",
        date: format(new Date(), "yyyy-MM-dd"),
        description: "",
        reference: ""
      });
      fetchAccountDetail(selectedAccount.guest_id);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen");
    }
  };

  const handleAddMonthlyRent = async () => {
    if (!selectedAccount) return;
    
    try {
      const response = await api.post(`/accounts/${selectedAccount.guest_id}/add-monthly-rent?month=${rentMonth}`);
      toast.success(response.data.message);
      setAddRentDialogOpen(false);
      fetchAccountDetail(selectedAccount.guest_id);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen der Miete");
    }
  };

  const handleDeleteTransaction = async (txId) => {
    if (!window.confirm("Transaktion wirklich löschen?")) return;
    
    try {
      await api.delete(`/accounts/transaction/${txId}`);
      toast.success("Transaktion gelöscht");
      fetchAccountDetail(selectedAccount.guest_id);
      fetchAccounts();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  // Edit Transaction
  const openEditTxDialog = (tx) => {
    setEditingTx(tx);
    setEditTxForm({
      type: tx.type,
      amount: tx.amount.toString(),
      date: tx.date,
      description: tx.description,
      reference: tx.reference || ""
    });
    setEditTxDialogOpen(true);
  };

  const handleEditTransaction = async (e) => {
    e.preventDefault();
    if (!editingTx) return;
    
    try {
      const response = await api.put(`/accounts/transaction/${editingTx.id}`, {
        type: editTxForm.type,
        amount: parseFloat(editTxForm.amount),
        date: editTxForm.date,
        description: editTxForm.description,
        reference: editTxForm.reference || null
      });
      
      toast.success("Transaktion aktualisiert");
      setEditTxDialogOpen(false);
      setEditingTx(null);
      fetchAccountDetail(selectedAccount.guest_id);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Aktualisieren");
    }
  };

  // Audit Log
  const fetchAuditLogs = async (guestId) => {
    setAuditLogsLoading(true);
    try {
      const response = await api.get(`/audit-logs/account/${guestId}`);
      setAuditLogs(response.data);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      // Audit logs admin only olabilir
      if (error.response?.status === 403) {
        toast.error("Keine Berechtigung für Änderungshistorie");
      } else {
        toast.error("Änderungshistorie konnte nicht geladen werden");
      }
      setAuditLogs([]);
    } finally {
      setAuditLogsLoading(false);
    }
  };

  const openAuditLogDialog = () => {
    if (selectedAccount) {
      fetchAuditLogs(selectedAccount.guest_id);
      setAuditLogDialogOpen(true);
    }
  };

  const formatAuditAction = (action) => {
    const actions = {
      create: { label: "Erstellt", color: "bg-green-100 text-green-800" },
      update: { label: "Geändert", color: "bg-blue-100 text-blue-800" },
      delete: { label: "Gelöscht", color: "bg-red-100 text-red-800" }
    };
    return actions[action] || { label: action, color: "bg-gray-100 text-gray-800" };
  };

  const fetchRentStatus = async (month) => {
    setRentStatusLoading(true);
    try {
      const response = await api.get(`/accounts/rent-status?month=${month}`);
      setRentStatus(response.data);
    } catch (error) {
      console.error("Error fetching rent status:", error);
      toast.error("Mietstatus konnte nicht geladen werden");
    } finally {
      setRentStatusLoading(false);
    }
  };

  const handleGenerateBulkRent = async () => {
    setGeneratingRent(true);
    try {
      const response = await api.post(`/accounts/generate-monthly-rent?month=${bulkRentMonth}`);
      const result = response.data;
      
      if (result.processed > 0) {
        toast.success(`${result.processed} Mieten gebucht (Gesamt: €${result.total_amount.toFixed(2)})`);
      }
      if (result.skipped > 0) {
        toast.info(`${result.skipped} übersprungen (bereits gebucht oder nicht fällig)`);
      }
      if (result.errors > 0) {
        toast.error(`${result.errors} Fehler aufgetreten`);
      }
      
      // Refresh data
      fetchAccounts();
      fetchRentStatus(bulkRentMonth);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Generieren der Mieten");
    } finally {
      setGeneratingRent(false);
    }
  };

  const handleDownloadPDF = async (guestId, guestName) => {
    try {
      const response = await api.get(`/accounts/${guestId}/statement/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Kontoauszug_${guestName.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Herunterladen");
    }
  };

  const handleDownloadExcel = async (guestId, guestName) => {
    try {
      const response = await api.get(`/accounts/${guestId}/statement/excel`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Kontoauszug_${guestName.replace(' ', '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Herunterladen");
    }
  };

  // PDF Önizleme fonksiyonları
  const handlePreviewPDF = async (guestId, guestName) => {
    setPdfPreviewLoading(true);
    setPdfPreviewGuest(guestName);
    setPdfPreviewOpen(true);
    
    try {
      const response = await api.get(`/accounts/${guestId}/statement/preview`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
    } catch (error) {
      console.error("PDF preview error:", error);
      toast.error("PDF konnte nicht geladen werden");
      setPdfPreviewOpen(false);
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  const closePdfPreview = () => {
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
    }
    setPdfPreviewUrl(null);
    setPdfPreviewOpen(false);
    setPdfPreviewGuest(null);
  };

  const filteredAccounts = accounts.filter(acc => 
    acc.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    acc.aktenzeichen?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getBalanceColor = (balance) => {
    if (balance > 0) return "text-emerald-600";
    if (balance < 0) return "text-red-600";
    return "text-slate-600";
  };

  const getBalanceBg = (balance) => {
    if (balance > 0) return "bg-emerald-50 border-emerald-200";
    if (balance < 0) return "bg-red-50 border-red-200";
    return "bg-slate-50 border-slate-200";
  };

  // Stats
  const totalPositive = accounts.filter(a => a.balance > 0).reduce((sum, a) => sum + a.balance, 0);
  const totalNegative = accounts.filter(a => a.balance < 0).reduce((sum, a) => sum + Math.abs(a.balance), 0);
  const accountsWithDebt = accounts.filter(a => a.balance < 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6" data-testid="accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Konten
          </h1>
          <p className="text-sm text-slate-500 mt-1">Bewohner-Kontoübersicht</p>
        </div>
        <Dialog open={generateRentDialogOpen} onOpenChange={(open) => {
          setGenerateRentDialogOpen(open);
          if (open) fetchRentStatus(bulkRentMonth);
        }}>
          <DialogTrigger asChild>
            <Button style={{ backgroundColor: BRAND_COLOR }} data-testid="generate-rent-btn">
              <Play className="h-4 w-4 mr-2" />
              Monatsmiete generieren
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Monatsmiete generieren
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Monat auswählen</Label>
                <Input
                  type="month"
                  value={bulkRentMonth}
                  onChange={(e) => {
                    setBulkRentMonth(e.target.value);
                    fetchRentStatus(e.target.value);
                  }}
                  data-testid="bulk-rent-month"
                />
              </div>
              
              {rentStatusLoading ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : rentStatus && (
                <div className="space-y-3">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="h-4 w-4 text-emerald-600" />
                        <span className="text-xs text-emerald-600 font-medium">Gebucht</span>
                      </div>
                      <p className="text-lg font-bold text-emerald-700">{rentStatus.charged?.length || 0}</p>
                      <p className="text-xs text-emerald-600">€{rentStatus.total_charged?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                        <span className="text-xs text-amber-600 font-medium">Offen</span>
                      </div>
                      <p className="text-lg font-bold text-amber-700">{rentStatus.not_charged?.length || 0}</p>
                      <p className="text-xs text-amber-600">
                        €{(rentStatus.not_charged || []).reduce((sum, n) => sum + (n.expected_amount || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Not charged list */}
                  {rentStatus.not_charged?.length > 0 && (
                    <div className="border border-slate-200 rounded-lg p-3 max-h-[200px] overflow-y-auto">
                      <p className="text-xs font-medium text-slate-600 mb-2">Offene Mieten:</p>
                      {rentStatus.not_charged.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm py-1 border-b border-slate-100 last:border-0">
                          <div>
                            <span className="text-slate-700">{item.guest_name}</span>
                            {item.charge_days && item.days_in_month && item.charge_days !== item.days_in_month && (
                              <span className="text-xs text-slate-500 ml-1">
                                ({item.charge_days}/{item.days_in_month} Tage)
                              </span>
                            )}
                          </div>
                          <span className="font-medium text-amber-600">€{item.expected_amount?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {rentStatus.not_charged?.length === 0 && (
                    <div className="text-center py-4 text-emerald-600 bg-emerald-50 rounded-lg">
                      <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                      <p className="text-sm font-medium">Alle Mieten für {bulkRentMonth} gebucht!</p>
                    </div>
                  )}
                </div>
              )}
              
              <Button
                className="w-full"
                style={{ backgroundColor: BRAND_COLOR }}
                onClick={handleGenerateBulkRent}
                disabled={generatingRent || (rentStatus && rentStatus.not_charged?.length === 0)}
                data-testid="confirm-generate-rent-btn"
              >
                {generatingRent ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generiere...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    {rentStatus?.not_charged?.length || 0} Mieten jetzt buchen
                  </>
                )}
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                Bereits gebuchte Mieten werden übersprungen.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Wallet className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Konten</p>
                <p className="text-xl font-bold text-slate-900">{accounts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-emerald-600">Guthaben</p>
                <p className="text-xl font-bold text-emerald-700">€{totalPositive.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <TrendingDown className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-red-600">Offene Beträge</p>
                <p className="text-xl font-bold text-red-700">€{totalNegative.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-amber-600">Mit Schulden</p>
                <p className="text-xl font-bold text-amber-700">{accountsWithDebt}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Suchen nach Name oder Aktenzeichen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="account-search"
        />
      </div>

      {/* Accounts Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredAccounts.map((account) => (
          <Card 
            key={account.guest_id}
            className={`cursor-pointer hover:shadow-md transition-shadow border ${getBalanceBg(account.balance)}`}
            onClick={() => openAccountDetail(account)}
            data-testid={`account-card-${account.guest_id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium text-slate-900">{account.guest_name}</h3>
                  {account.aktenzeichen && (
                    <p className="text-xs text-slate-500 font-mono">{account.aktenzeichen}</p>
                  )}
                </div>
                <Wallet className={`h-5 w-5 ${getBalanceColor(account.balance)}`} />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Soll</span>
                  <span className="text-red-600 font-medium">€{account.total_debit.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Haben</span>
                  <span className="text-emerald-600 font-medium">€{account.total_credit.toFixed(2)}</span>
                </div>
                <div className="pt-2 border-t border-current/10">
                  <div className="flex justify-between">
                    <span className="font-medium text-slate-700">Saldo</span>
                    <span className={`font-bold text-lg ${getBalanceColor(account.balance)}`}>
                      {account.balance >= 0 ? '+' : ''}€{account.balance.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              
              {account.transaction_count > 0 && (
                <p className="text-xs text-slate-400 mt-2">
                  {account.transaction_count} Transaktionen
                </p>
              )}
              
              {/* Action Buttons */}
              <div className="grid grid-cols-3 gap-1.5 mt-3 pt-3 border-t border-slate-200">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs px-1.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreviewPDF(account.guest_id, account.guest_name);
                  }}
                  data-testid={`preview-pdf-${account.guest_id}`}
                >
                  <Eye className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Ansicht</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs px-1.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadPDF(account.guest_id, account.guest_name);
                  }}
                  data-testid={`download-pdf-${account.guest_id}`}
                >
                  <Download className="h-3 w-3 sm:mr-1" />
                  <span>PDF</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs px-1.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadExcel(account.guest_id, account.guest_name);
                  }}
                  data-testid={`download-excel-${account.guest_id}`}
                >
                  <FileSpreadsheet className="h-3 w-3 sm:mr-1" />
                  <span>Excel</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAccounts.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Wallet className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p>Keine Konten gefunden</p>
        </div>
      )}

      {/* Account Detail Dialog */}
      <Dialog open={!!selectedAccount} onOpenChange={(open) => !open && setSelectedAccount(null)}>
        <DialogContent className="max-w-2xl mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Wallet className="h-5 w-5" />
              Kontodetails
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : accountDetail && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium text-lg">{accountDetail.guest_name}</h3>
                {accountDetail.aktenzeichen && (
                  <p className="text-sm text-slate-500 font-mono">{accountDetail.aktenzeichen}</p>
                )}
                {accountDetail.monthly_rent > 0 && (
                  <p className="text-sm text-slate-600 mt-1">
                    Monatsmiete: <span className="font-medium">€{accountDetail.monthly_rent.toFixed(2)}</span>
                  </p>
                )}
              </div>

              {/* Balance Summary */}
              <div className={`p-4 rounded-lg border ${getBalanceBg(accountDetail.balance)}`}>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-red-600 mb-1">Soll</p>
                    <p className="text-lg font-bold text-red-700">€{accountDetail.total_debit.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-emerald-600 mb-1">Haben</p>
                    <p className="text-lg font-bold text-emerald-700">€{accountDetail.total_credit.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Saldo</p>
                    <p className={`text-xl font-bold ${getBalanceColor(accountDetail.balance)}`}>
                      {accountDetail.balance >= 0 ? '+' : ''}€{accountDetail.balance.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Dialog open={addTxDialogOpen} onOpenChange={setAddTxDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="flex-1">
                      <Plus className="h-4 w-4 mr-2" />
                      Transaktion
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Neue Transaktion</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddTransaction} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Typ</Label>
                        <Select value={txForm.type} onValueChange={(v) => setTxForm({...txForm, type: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="credit">Haben (Einzahlung/Gutschrift)</SelectItem>
                            <SelectItem value="debit">Soll (Belastung/Miete)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Betrag (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={txForm.amount}
                          onChange={(e) => setTxForm({...txForm, amount: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Datum</Label>
                        <Input
                          type="date"
                          value={txForm.date}
                          onChange={(e) => setTxForm({...txForm, date: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Beschreibung</Label>
                        <Input
                          value={txForm.description}
                          onChange={(e) => setTxForm({...txForm, description: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Referenz (optional)</Label>
                        <Input
                          value={txForm.reference}
                          onChange={(e) => setTxForm({...txForm, reference: e.target.value})}
                        />
                      </div>
                      <Button type="submit" className="w-full" style={{ backgroundColor: BRAND_COLOR }}>
                        Hinzufügen
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog open={addRentDialogOpen} onOpenChange={setAddRentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="flex-1">
                      <Calendar className="h-4 w-4 mr-2" />
                      Monatsmiete
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Monatsmiete hinzufügen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Monat</Label>
                        <Input
                          type="month"
                          value={rentMonth}
                          onChange={(e) => setRentMonth(e.target.value)}
                        />
                      </div>
                      <p className="text-sm text-slate-600">
                        Monatsmiete: <span className="font-medium">€{accountDetail.monthly_rent.toFixed(2)}</span>
                      </p>
                      <Button 
                        className="w-full" 
                        style={{ backgroundColor: BRAND_COLOR }}
                        onClick={handleAddMonthlyRent}
                        disabled={accountDetail.monthly_rent <= 0}
                      >
                        Miete buchen (€{accountDetail.monthly_rent.toFixed(2)})
                      </Button>
                      {accountDetail.monthly_rent <= 0 && (
                        <p className="text-xs text-amber-600">
                          Keine Monatsmiete definiert. Bitte zuerst eine Aufnahme erstellen.
                        </p>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* Audit Log Button */}
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={openAuditLogDialog}
                >
                  <History className="h-4 w-4 mr-2" />
                  Historie
                </Button>
              </div>

              {/* Transactions List */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Transaktionen</h4>
                {accountDetail.transactions?.length > 0 ? (
                  <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Datum</TableHead>
                          <TableHead className="text-xs">Beschreibung</TableHead>
                          <TableHead className="text-xs text-right">Soll</TableHead>
                          <TableHead className="text-xs text-right">Haben</TableHead>
                          <TableHead className="text-xs w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {accountDetail.transactions.map((tx) => (
                          <TableRow key={tx.id} className="hover:bg-slate-50">
                            <TableCell className="text-xs text-slate-600">{tx.date}</TableCell>
                            <TableCell className="text-xs">
                              <div className="truncate max-w-[150px]" title={tx.description}>
                                {tx.description}
                              </div>
                              {tx.source && tx.source !== 'manual' && (
                                <Badge variant="outline" className="text-xs mt-1">
                                  {tx.source === 'csv_import' ? 'CSV' : tx.source === 'auto_rent' ? 'Auto' : String(tx.source)}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono">
                              {tx.type === 'debit' ? (
                                <span className="text-red-600 flex items-center justify-end">
                                  <ArrowDownRight className="h-3 w-3 mr-1" />
                                  €{tx.amount.toFixed(2)}
                                </span>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono">
                              {tx.type === 'credit' ? (
                                <span className="text-emerald-600 flex items-center justify-end">
                                  <ArrowUpRight className="h-3 w-3 mr-1" />
                                  €{tx.amount.toFixed(2)}
                                </span>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700"
                                  onClick={() => openEditTxDialog(tx)}
                                  title="Bearbeiten"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteTransaction(tx.id)}
                                  title="Löschen"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                    <FileText className="h-8 w-8 mx-auto mb-2" />
                    <p className="text-sm">Keine Transaktionen</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog open={pdfPreviewOpen} onOpenChange={(open) => !open && closePdfPreview()}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden" hideCloseButton>
          <DialogHeader className="sr-only">
            <DialogTitle>Kontoauszug Vorschau</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-slate-50">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-slate-600" />
                <div>
                  <h3 className="font-semibold text-slate-900">Kontoauszug</h3>
                  {pdfPreviewGuest && (
                    <p className="text-sm text-slate-500">{pdfPreviewGuest}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (pdfPreviewUrl) {
                      const link = document.createElement('a');
                      link.href = pdfPreviewUrl;
                      link.setAttribute('download', `Kontoauszug_${pdfPreviewGuest?.replace(' ', '_')}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success("PDF heruntergeladen");
                    }
                  }}
                  data-testid="pdf-preview-download"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Herunterladen
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (pdfPreviewUrl) {
                      window.open(pdfPreviewUrl, '_blank');
                    }
                  }}
                  data-testid="pdf-preview-newtab"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Neues Tab
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closePdfPreview}
                  data-testid="pdf-preview-close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* PDF Viewer */}
            <div className="flex-1 bg-slate-100 relative">
              {pdfPreviewLoading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                    <p className="text-sm text-slate-500">PDF wird geladen...</p>
                  </div>
                </div>
              ) : pdfPreviewUrl ? (
                <object
                  data={pdfPreviewUrl}
                  type="application/pdf"
                  className="w-full h-full"
                  data-testid="pdf-preview-object"
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center p-6 bg-white rounded-lg shadow-lg max-w-md">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-600 mb-4">PDF-Vorschau wird von Ihrem Browser nicht unterstützt.</p>
                      <Button
                        onClick={() => {
                          if (pdfPreviewUrl) {
                            const link = document.createElement('a');
                            link.href = pdfPreviewUrl;
                            link.setAttribute('download', `Kontoauszug_${pdfPreviewGuest?.replace(' ', '_')}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            toast.success("PDF heruntergeladen");
                          }
                        }}
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        PDF herunterladen
                      </Button>
                    </div>
                  </div>
                </object>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>PDF konnte nicht geladen werden</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <Dialog open={editTxDialogOpen} onOpenChange={setEditTxDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Transaktion bearbeiten
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditTransaction} className="space-y-4">
            <div className="space-y-2">
              <Label>Typ</Label>
              <Select 
                value={editTxForm.type} 
                onValueChange={(v) => setEditTxForm(prev => ({...prev, type: v}))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">
                    <span className="flex items-center gap-2">
                      <ArrowUpRight className="h-4 w-4 text-emerald-600" />
                      Haben (Einzahlung)
                    </span>
                  </SelectItem>
                  <SelectItem value="debit">
                    <span className="flex items-center gap-2">
                      <ArrowDownRight className="h-4 w-4 text-red-600" />
                      Soll (Belastung)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Betrag (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={editTxForm.amount}
                onChange={(e) => setEditTxForm(prev => ({...prev, amount: e.target.value}))}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Datum</Label>
              <Input
                type="date"
                value={editTxForm.date}
                onChange={(e) => setEditTxForm(prev => ({...prev, date: e.target.value}))}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Beschreibung</Label>
              <Input
                value={editTxForm.description}
                onChange={(e) => setEditTxForm(prev => ({...prev, description: e.target.value}))}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Referenz (optional)</Label>
              <Input
                value={editTxForm.reference}
                onChange={(e) => setEditTxForm(prev => ({...prev, reference: e.target.value}))}
                placeholder="z.B. Überweisungsnummer"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditTxDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" style={{ backgroundColor: BRAND_COLOR }}>
                Speichern
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditLogDialogOpen} onOpenChange={setAuditLogDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Änderungshistorie
              {selectedAccount && (
                <span className="text-sm font-normal text-slate-500 ml-2">
                  - {selectedAccount.guest_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[60vh]">
            {auditLogsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : auditLogs.length > 0 ? (
              <div className="space-y-3">
                {auditLogs.map((log) => {
                  const actionStyle = formatAuditAction(log.action);
                  return (
                    <div key={log.id} className="border rounded-lg p-3 bg-slate-50">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={actionStyle.color}>
                            {actionStyle.label}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {format(new Date(log.timestamp), "dd.MM.yyyy HH:mm", { locale: de })}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <User className="h-3 w-3" />
                          {log.user_name}
                        </div>
                      </div>
                      
                      {log.details && (
                        <p className="text-sm text-slate-700 mb-2">{log.details}</p>
                      )}
                      
                      {log.action === 'update' && log.changes && Object.keys(log.changes).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {Object.entries(log.changes).map(([field, change]) => (
                            <div key={field} className="text-xs bg-white p-2 rounded border">
                              <span className="font-medium text-slate-600">{field}:</span>
                              <span className="text-red-500 line-through ml-2">{String(change.old)}</span>
                              <span className="text-slate-400 mx-1">→</span>
                              <span className="text-green-600">{String(change.new)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <History className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>Keine Änderungen gefunden</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
